import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { storeAuthData } from "@/utils/auth";
import Navbar from "@/components/navbar";

/**
 * Magic Link Verification Page
 *
 * Route: /auth/magic-link?token=...
 *
 * Shows a loading animation while verifying the magic link token,
 * then redirects to the appropriate page on success or shows an error.
 */
export default function MagicLinkVerify() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("verifying"); // verifying | success | error
  const [errorMessage, setErrorMessage] = useState("");
  const verificationStartedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate verification runs
    if (verificationStartedRef.current) {
      return;
    }

    // Check if accessing from allowed portal subdomain
    const hostname = window.location.hostname;
    const isPortalSubdomain =
      hostname.startsWith("portal.") || hostname.startsWith("portal-");

    if (!isPortalSubdomain) {
      // Redirect to login if not on portal subdomain
      window.location.href = "/login";
      return;
    }

    const token = searchParams.get("token");

    if (!token) {
      setStatus("error");
      setErrorMessage(
        "No magic link token provided. Please request a new link.",
      );
      return;
    }

    verificationStartedRef.current = true;
    verifyMagicLink(token);
  }, [searchParams]);

  const verifyMagicLink = async (token) => {
    try {
      // Call the Vercel API route which proxies to the Cloud Function with internal secret
      const response = await fetch("/api/magic-link-verify", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        setStatus("error");
        setErrorMessage(
          data.error ||
            "Invalid or expired magic link. Please request a new one.",
        );
        return;
      }

      // Verification successful - get auth data
      const { token: authToken, customer } = data;
      const customerId = customer?.id || "";
      const email = customer?.email || "";
      const phone = customer?.phone || "";
      const onboarding = customer?.onboarding || false;

      // Don't store locally - we'll pass it via URL to the main domain
      setStatus("success");

      // Determine main domain URL (remove portal. subdomain)
      const hostname = window.location.hostname;
      const protocol = window.location.protocol;
      const port = window.location.port;
      let mainHost;

      if (hostname.startsWith("portal.")) {
        // Remove 'portal.' prefix to get main domain
        mainHost = hostname.replace("portal.", "");
      } else if (hostname.startsWith("portal-")) {
        // Handle portal-localhost or similar
        mainHost = hostname.replace("portal-", "");
      } else {
        // Fallback to current host
        mainHost = hostname;
      }

      // Build main domain URL with port if present
      const mainDomain = port
        ? `${protocol}//${mainHost}:${port}`
        : `${protocol}//${mainHost}`;

      // Build auth callback URL with token and data
      // This will be handled by a callback route on the main domain
      const authData = {
        token: authToken,
        customerId,
        email,
        phone,
        onboarding: onboarding ? "1" : "0",
      };
      const authParams = new URLSearchParams(authData).toString();

      // Determine redirect target
      let redirectTarget = "/todays-tips";
      if (onboarding) {
        redirectTarget = "/onboard";
      } else {
        const savedRedirect = sessionStorage.getItem("redirectAfterLogin");
        if (savedRedirect) {
          redirectTarget = savedRedirect;
        }
      }

      // Brief delay to show success message, then redirect to auth callback
      setTimeout(() => {
        window.location.href = `${mainDomain}/auth/callback?${authParams}&redirect=${encodeURIComponent(redirectTarget)}`;
      }, 1500);
    } catch (error) {
      console.error("[MagicLinkVerify] Error:", error);
      setStatus("error");
      setErrorMessage(
        "Something went wrong. Please try again or request a new link.",
      );
    }
  };

  return (
    <div className="min-h-screen flex flex-col pb-[95px] pt-[64px] mx-auto bg-[#F5F5F5]">
      {/* <Navbar /> */}
      <div className="flex flex-col items-center px-4 flex-1 justify-center">
        {status === "verifying" && (
          <>
            {/* Animated Loading Icon */}
            <div className="w-20 h-20 bg-[#FCEDF7] rounded-full flex items-center justify-center mb-6 relative">
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-[#F0A7D6] animate-spin"></div>
              <svg
                className="w-10 h-10 text-[#F0A7D6]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-2">Verifying Magic Link</h1>
            <p className="text-gray-600 text-center">
              Please wait while we verify your sign-in link...
            </p>
          </>
        )}

        {status === "success" && (
          <>
            {/* Success Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-10 h-10 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-2 text-green-600">Success!</h1>
            <p className="text-gray-600 text-center">
              You're signed in. Redirecting you now...
            </p>
          </>
        )}

        {status === "error" && (
          <>
            {/* Error Icon */}
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <svg
                className="w-10 h-10 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold mb-2 text-red-600">
              Verification Failed
            </h1>
            <p className="text-gray-600 text-center mb-6">{errorMessage}</p>

            <a
              href="/login"
              className="w-full max-w-sm bg-brand-pink text-white py-3 rounded-lg font-medium hover:bg-[#e391c4] transition-all duration-200 text-center"
            >
              Back to Login
            </a>
          </>
        )}
      </div>
    </div>
  );
}
